import React from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="privacy-policy"
        defaultTitle="Privacy Policy | House Plan Direct"
        defaultDescription="House Plan Direct Privacy Policy. Learn how we collect, use, and protect your personal data in compliance with UK GDPR and Data Protection Act 2018."
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8" data-testid="privacy-title">Privacy Policy</h1>
        
        <div className="prose prose-gray max-w-none space-y-6 text-gray-700">
          <p className="text-sm text-gray-500">
            <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p>House Plan Direct ("we", "us", "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, store, and protect your personal data in accordance with:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>UK General Data Protection Regulation (UK GDPR)</li>
              <li>Data Protection Act 2018</li>
              <li>Privacy and Electronic Communications Regulations (PECR)</li>
              <li>Information Commissioner's Office (ICO) guidance</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Data Controller</h2>
            <p><strong>Controller Name:</strong> House Plan Direct</p>
            <p><strong>Business:</strong> Digital floor plans, plan conversions, redraws, and architectural-style plan preparation</p>
            <p><strong>Contact Email:</strong> <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline">web@houseplandirect.com</a></p>
            <p><strong>Postal Address:</strong> House Plan Direct, PO Box 686, Exeter, EX2 9PD, United Kingdom</p>
            <p><strong>Operating Region:</strong> United Kingdom</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. What Personal Data We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.1 Information You Provide</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Contact Details:</strong> Name, email address, phone number, postal address</li>
              <li><strong>Property Information:</strong> Property address, property type, room details</li>
              <li><strong>Digital Assets:</strong> Property photos, floor plan sketches, existing plans, measurements, and any images you upload</li>
              <li><strong>Order Details:</strong> Service requirements, specifications, notes, and preferences</li>
              <li><strong>Payment Information:</strong> Payment preferences (PayPal account details handled by PayPal - we do not store card details)</li>
              <li><strong>Communications:</strong> Emails, messages, and correspondence with us</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">3.2 Information We Collect Automatically</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Usage Data:</strong> IP address, browser type, device information, pages visited, time spent on pages</li>
              <li><strong>Cookies:</strong> As described in our Cookie Policy (see Section 10)</li>
              <li><strong>Analytics Data:</strong> Website usage statistics via Google Analytics (when configured)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Lawful Basis for Processing</h2>
            <p>We process your personal data under the following lawful bases:</p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.1 Contract (Article 6(1)(b) UK GDPR)</h3>
            <p>Processing necessary to perform our contract with you, including:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Processing your order</li>
              <li>Creating and delivering your floor plans</li>
              <li>Communicating about your order</li>
              <li>Processing payments</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.2 Legitimate Interest (Article 6(1)(f) UK GDPR)</h3>
            <p>Processing necessary for our legitimate business interests:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Improving our services and website</li>
              <li>Fraud prevention and security</li>
              <li>Marketing to existing customers (with opt-out option)</li>
              <li>Business analytics and performance monitoring</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.3 Legal Obligation (Article 6(1)(c) UK GDPR)</h3>
            <p>Processing required by law:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Accounting and tax records (6 years retention)</li>
              <li>Compliance with financial regulations</li>
              <li>Responding to legal requests</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.4 Consent (Article 6(1)(a) UK GDPR)</h3>
            <p>Processing based on your explicit consent:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Marketing communications (newsletter)</li>
              <li>Non-essential cookies and tracking</li>
              <li>Testimonials or case studies (if applicable)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. How We Use Your Personal Data</h2>
            <ul className="list-disc ml-6 space-y-2">
              <li>To process and fulfil your orders</li>
              <li>To create professional floor plans from your sketches</li>
              <li>To communicate with you about your order and services</li>
              <li>To process payments securely</li>
              <li>To improve our website and services</li>
              <li>To comply with legal and regulatory requirements</li>
              <li>To prevent fraud and ensure website security</li>
              <li>To send service updates (essential communications)</li>
              <li>To send marketing communications (with your consent)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Data Sharing and Third Parties</h2>
            <p>We may share your personal data with trusted third parties who assist us in operating our business:</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">6.1 Service Providers</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Hosting Provider:</strong> For website and data storage (GDPR-compliant hosting)</li>
              <li><strong>Payment Processor:</strong> PayPal (for secure payment processing)</li>
              <li><strong>Email Service Provider:</strong> For sending order confirmations and communications</li>
              <li><strong>Analytics Provider:</strong> Google Analytics (when configured, with IP anonymisation)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">6.2 Legal Requirements</h3>
            <p>We may disclose your data if required by law, court order, or regulatory authority.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">6.3 Data Protection Measures</h3>
            <p>All third parties are carefully selected and required to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Comply with UK GDPR and data protection laws</li>
              <li>Process data only on our instructions</li>
              <li>Implement appropriate security measures</li>
              <li>Sign Data Processing Agreements where required</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. International Data Transfers</h2>
            <p>Your data is primarily stored and processed within the United Kingdom. If we transfer data outside the UK/EEA, we ensure:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Adequate safeguards are in place (Standard Contractual Clauses)</li>
              <li>The recipient country has an adequacy decision from the UK</li>
              <li>Appropriate security measures are implemented</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Data Retention Periods</h2>
            <p>We retain your personal data only for as long as necessary:</p>

            <table className="min-w-full border border-gray-300 mt-4">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-300 px-4 py-2 text-left">Data Type</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Retention Period</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Customer contact details</td>
                  <td className="border border-gray-300 px-4 py-2">6 years after last transaction</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Floor plans and project files</td>
                  <td className="border border-gray-300 px-4 py-2">6 years (or as specified)</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Uploaded images/sketches</td>
                  <td className="border border-gray-300 px-4 py-2">Deleted after project completion or 90 days</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Financial records</td>
                  <td className="border border-gray-300 px-4 py-2">6 years (legal requirement)</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Email correspondence</td>
                  <td className="border border-gray-300 px-4 py-2">Up to 6 years</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Marketing consent</td>
                  <td className="border border-gray-300 px-4 py-2">Until withdrawn or 2 years inactivity</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Analytics data</td>
                  <td className="border border-gray-300 px-4 py-2">26 months (Google Analytics default)</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Your Data Protection Rights</h2>
            <p>Under UK GDPR, you have the following rights:</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">9.1 Right of Access (Article 15)</h3>
            <p>Request a copy of the personal data we hold about you.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">9.2 Right to Rectification (Article 16)</h3>
            <p>Request correction of inaccurate or incomplete data.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">9.3 Right to Erasure (Article 17)</h3>
            <p>Request deletion of your personal data (subject to legal retention requirements).</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">9.4 Right to Restriction (Article 18)</h3>
            <p>Request temporary restriction of processing while disputes are resolved.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">9.5 Right to Data Portability (Article 20)</h3>
            <p>Receive your data in a structured, machine-readable format.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">9.6 Right to Object (Article 21)</h3>
            <p>Object to processing based on legitimate interests or direct marketing.</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">9.7 Right to Withdraw Consent (Article 7)</h3>
            <p>Withdraw consent for processing at any time (doesn't affect prior lawful processing).</p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">How to Exercise Your Rights</h3>
            <p>To exercise any of these rights, please contact us at: <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline">web@houseplandirect.com</a></p>
            <p>We will respond within one month. If your request is complex, we may extend this by two months and will notify you.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar technologies to enhance your website experience. For detailed information, please see our separate Cookie Policy.</p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Types of Cookies We Use:</h3>
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>Essential Cookies:</strong> Required for website functionality (no consent needed)</li>
              <li><strong>Analytics Cookies:</strong> Google Analytics (requires consent)</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences</li>
            </ul>

            <p className="mt-4">You can manage cookie preferences through your browser settings or our cookie consent banner.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Security Measures</h2>
            <p>We implement appropriate technical and organisational measures to protect your personal data:</p>
            
            <ul className="list-disc ml-6 space-y-2">
              <li><strong>HTTPS Encryption:</strong> All data transmitted is encrypted using SSL/TLS</li>
              <li><strong>Secure Hosting:</strong> Data stored on secure, UK-based servers with backup systems</li>
              <li><strong>Access Controls:</strong> Strict authentication and authorization procedures</li>
              <li><strong>Employee Training:</strong> Staff trained in data protection and confidentiality</li>
              <li><strong>Regular Security Audits:</strong> Ongoing monitoring and security assessments</li>
              <li><strong>Data Minimisation:</strong> We collect only essential data</li>
              <li><strong>Password Protection:</strong> Encrypted storage of passwords using industry-standard hashing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Data Breach Notification</h2>
            <p>In the unlikely event of a personal data breach that poses a risk to your rights and freedoms, we will:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Notify the ICO within 72 hours of becoming aware</li>
              <li>Notify affected individuals without undue delay if there is a high risk</li>
              <li>Provide clear information about the breach and steps being taken</li>
              <li>Offer guidance on protecting yourself from potential harm</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Children's Privacy</h2>
            <p>Our services are not intended for children under 16. We do not knowingly collect personal data from children. If we become aware that we have collected data from a child without parental consent, we will delete it promptly.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Marketing Communications</h2>
            <p>With your consent, we may send you marketing communications about:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>New services and features</li>
              <li>Special offers and promotions</li>
              <li>Industry news and tips</li>
            </ul>
            
            <p className="mt-4"><strong>Unsubscribe:</strong> You can opt out at any time by:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Clicking the unsubscribe link in any marketing email</li>
              <li>Contacting us at web@houseplandirect.com</li>
              <li>Updating your preferences in your account (if applicable)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">15. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time to reflect:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Changes in law or regulation</li>
              <li>Changes to our services</li>
              <li>Best practice recommendations</li>
            </ul>
            
            <p className="mt-4">The "Last Updated" date at the top of this page shows when this policy was last revised. We will notify you of significant changes via email or a prominent notice on our website.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">16. Complaints and Supervisory Authority</h2>
            <p>If you have concerns about how we handle your personal data, please contact us first at: <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline">web@houseplandirect.com</a></p>
            
            <p className="mt-4">You also have the right to lodge a complaint with the UK supervisory authority:</p>
            
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4">
              <p><strong>Information Commissioner's Office (ICO)</strong></p>
              <p>Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://ico.org.uk</a></p>
              <p>Telephone: 0303 123 1113</p>
              <p>Live Chat: Available on ICO website</p>
              <p>Postal Address: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">17. Contact Us</h2>
            <p>For any questions about this Privacy Policy or how we handle your personal data, please contact us:</p>
            
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mt-4">
              <p><strong>House Plan Direct</strong></p>
              <p>Email: <a href="mailto:web@houseplandirect.com" className="text-blue-600 hover:underline">web@houseplandirect.com</a></p>
              <p>Postal Address: House Plan Direct, PO Box 686, Exeter, EX2 9PD, United Kingdom</p>
            </div>
          </section>

          <section className="mt-12 p-6 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm font-semibold">
              This Privacy Policy is compliant with UK GDPR, Data Protection Act 2018, and ICO guidance as of the date stated above.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
